import React, { useRef, useState, useEffect } from "react";
import CommonTitle from "../common/DeliveryCommonTitle";
import {
  uploadShippingLabelsAPI,
  convertToTinyURLs,
  sendDeliverySessionAPI,
  sendPreApproveStatusAPI,
  sendMessageAPI,
} from "../../services/delivery_api";
import { useDeliverySession } from "../../hooks/useDeliverySession";
import LoadingSpinner from "../common/LoadingSpinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

const ShippingLabelCaptureScreen = ({
  onBack,
  onNext,
  parcelCount,
  residentDetails,
}) => {
  const inputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [imageSizes, setImageSizes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { session, setSession } = useDeliverySession();
  const { t } = useTranslation();

  const [persistedParcelCount, setPersistedParcelCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log("=== ShippingLabelCaptureScreen Debug ===");
    console.log("Received residentDetails:", residentDetails);
    console.log("Received parcelCount prop:", parcelCount);
    console.log("Full session:", session);
    console.log("Session deliveries:", session?.deliveries);
  }, [residentDetails, parcelCount, session]);

  // Find the current resident from session if residentDetails is null
  const findCurrentResident = () => {
    if (!session?.deliveries?.length) return null;

    // If we have residentDetails with resident_id, use that
    if (residentDetails?.resident_id) {
      const resident = session.deliveries.find(
        (d) => d.resident_token === residentDetails.resident_id,
      );
      console.log("Found resident by resident_id:", resident);

      if (resident) {
        return resident;
      }
    }

    // Find the most recent resident without captured images
    const residentsWithoutImages = session.deliveries.filter(
      (d) =>
        !d.captured_images ||
        !d.captured_images.links ||
        d.captured_images.links.length === 0,
    );

    if (residentsWithoutImages.length > 0) {
      console.log("Found resident without images:", residentsWithoutImages[0]);
      return residentsWithoutImages[0];
    }

    // Return the last resident
    const lastResident = session.deliveries[session.deliveries.length - 1];
    console.log("Using last resident:", lastResident);
    return lastResident;
  };

  // Get resident token from session if not provided
  const residentToken = React.useMemo(() => {
    // if (residentDetails?.resident_id) {
    //   return residentDetails.resident_id;
    // }

    const currentResident = findCurrentResident();
    console.log(
      "Determined resident for token:",
      currentResident?.resident_token,
    );
    return currentResident?.resident_token || null;
  }, [residentDetails, session]);

  // Initialize parcel count
  useEffect(() => {
    console.log("=== Initializing parcel count ===");

    const currentResident = findCurrentResident();
    console.log("Current resident found:", currentResident);

    // Priority 1: Use parcelCount prop
    if (parcelCount && !isNaN(parcelCount) && parcelCount > 0) {
      console.log("✅ Using parcelCount prop:", parcelCount);
      setPersistedParcelCount(parcelCount);
      return;
    }

    // Priority 2: Use resident's parcel count from session
    if (currentResident?.number_of_parcel) {
      const residentParcelCount = parseInt(
        currentResident.number_of_parcel,
        10,
      );
      if (!isNaN(residentParcelCount) && residentParcelCount > 0) {
        console.log("✅ Using resident parcel count:", residentParcelCount);
        setPersistedParcelCount(residentParcelCount);
        return;
      }
    }

    // Priority 3: Use residentDetails parcel_count
    if (residentDetails?.parcel_count) {
      const detailsParcelCount = parseInt(residentDetails.parcel_count, 10);
      if (!isNaN(detailsParcelCount) && detailsParcelCount > 0) {
        console.log(
          "✅ Using residentDetails parcel_count:",
          detailsParcelCount,
        );
        setPersistedParcelCount(detailsParcelCount);
        return;
      }
    }

    // Default
    console.log("⚠️ Defaulting to 1 parcel");
    setPersistedParcelCount(1);
  }, [parcelCount, residentDetails, session]);

  // Initialize image slots
  useEffect(() => {
    console.log("persistedParcelCount changed to:", persistedParcelCount);

    if (persistedParcelCount > 0) {
      const count = parseInt(persistedParcelCount, 10);
      console.log("Creating arrays with", count, "slots");
      setImages(Array(count).fill(null));
      setPreviews(Array(count).fill(null));
      setImageSizes(Array(count).fill(null));
      setCurrentIndex(0);
    } else {
      setImages([]);
      setPreviews([]);
      setImageSizes([]);
      setCurrentIndex(0);
    }
  }, [persistedParcelCount]);

  // Auto-open camera
  useEffect(() => {
    if (images.length > 0 && !images[currentIndex]) {
      setTimeout(() => {
        inputRef.current?.click();
      }, 300);
    }
  }, [currentIndex, images]);

  // Cleanup
  useEffect(() => {
    return () => {
      previews.forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, [previews]);

  const openCamera = () => {
    if (currentIndex < persistedParcelCount) {
      inputRef.current?.click();
    }
  };

  const validateSingleImageSize = (file, index) => {
    const MAX_SIZE_MB = 5;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (parseFloat(fileSizeMB) > MAX_SIZE_MB) {
      return {
        valid: false,
        message: `Image ${
          index + 1
        } is too large (${fileSizeMB} MB). Max: 5 MB.`,
        size: fileSizeMB,
      };
    }
    return { valid: true, message: "OK", size: fileSizeMB };
  };

  const handleCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateSingleImageSize(file, currentIndex);
    if (!validation.valid) {
      toast.error(validation.message, { autoClose: 5000 });
      e.target.value = "";
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const newImages = [...images];
    newImages[currentIndex] = file;
    setImages(newImages);

    const newPreviews = [...previews];
    newPreviews[currentIndex] = previewUrl;
    setPreviews(newPreviews);

    const newImageSizes = [...imageSizes];
    newImageSizes[currentIndex] = validation.size;
    setImageSizes(newImageSizes);

    e.target.value = "";
  };

  const handleNext = async () => {
    const total = parseInt(persistedParcelCount, 10);

    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }

    // Check if all images are captured
    const capturedCount = images.filter((img) => img !== null).length;
    if (capturedCount < total) {
      toast.error(`Please capture all ${total} labels before uploading.`);
      const firstMissingIndex = images.findIndex((img) => img === null);
      if (firstMissingIndex !== -1) {
        setCurrentIndex(firstMissingIndex);
      }
      return;
    }

    if (isLoading || uploadInProgress) {
      toast.warn("Upload in progress...");
      return;
    }

    setIsLoading(true);
    setUploadInProgress(true);

    console.log("=== Starting upload ===");
    console.log("Resident token:", residentToken);

    // Find resident in session
    const currentResident = findCurrentResident();

    if (!currentResident) {
      toast.error("No resident found. Please go back and try again.");
      setIsLoading(false);
      setUploadInProgress(false);
      return;
    }

    console.log("Current resident for upload:", currentResident);

    const formData = new FormData();
    images.forEach((img, index) => {
      if (!img) {
        console.log(`Image [${index}] is null or undefined`);
        return;
      }

      console.log(`Image [${index}] details:`, {
        uri: img.uri,
        name: img.name,
        type: img.type,
        size: img.size, // if available
      });

      formData.append("shipping_labels", img);
    });
    formData.append("file_ident_code", session.file_ident_code);
    formData.append("da_id", session.da_id.toString());
    formData.append(
      "resident_id",
      currentResident.resident_id?.toString() || "",
    );
    formData.append("resident_name", currentResident.resident_name || "");

    for (let pair of formData.entries()) {
      console.log(
        "FormData →",
        pair[0],
        pair[1] instanceof File
          ? {
              name: pair[1].name,
              type: pair[1].type,
              size: (pair[1].size / 1024).toFixed(2) + " KB",
            }
          : pair[1],
      );
    }

    try {
      const result = await uploadShippingLabelsAPI(formData);
      // const tinyUrls = await convertToTinyURLs(result.data.shipping_label_urls);

      // const tinyUrls = await shortenUrl(result.data.shipping_label_urls);

      const capturedImagesLinks = result.data.shipping_label_urls.map(
        (url, i) => ({
          public_url: url,
          shortlink: "",
        }),
      );

      // Update session
      const existingIndex = session.deliveries.findIndex(
        (d) => d.resident_token === currentResident.resident_token,
      );

      let updatedDeliveries;
      if (existingIndex !== -1) {
        updatedDeliveries = session.deliveries.map((delivery, index) =>
          index === existingIndex
            ? {
                ...delivery,
                number_of_parcel: persistedParcelCount.toString(),
                captured_images: {
                  links: capturedImagesLinks,
                },
              }
            : delivery,
        );
      } else {
        const newDelivery = {
          ...currentResident,
          number_of_parcel: persistedParcelCount.toString(),
          captured_images: { links: capturedImagesLinks },
        };
        updatedDeliveries = [...session.deliveries, newDelivery];
      }

      const updatedSession = { ...session, deliveries: updatedDeliveries };
      setSession(updatedSession);

      // Send to backend
      const sessionResponse = await sendDeliverySessionAPI(
        JSON.stringify(updatedSession, null, 2),
      );

      // Update session with response data
      if (sessionResponse && sessionResponse.success && sessionResponse.data) {
        // Update the main session fields
        setSession((prev) => ({
          ...prev,
          delivery_qr_code_shortlink:
            sessionResponse.data.delivery_qr_code_shortlink ||
            prev.delivery_qr_code_shortlink,
          pending_delivery_id:
            sessionResponse.data.pending_delivery_id ||
            prev.pending_delivery_id,
          web_status: sessionResponse.data.web_status || prev.web_status,
          dp_web_session_otp:
            sessionResponse.data.dp_web_session_otp || prev.dp_web_session_otp, // Add this line
        }));

        // Or use the updateDeliveryField function if you prefer
        // updateDeliveryField("delivery_qr_code_shortlink", sessionResponse.data.qr_code_url);
        // updateDeliveryField("pending_delivery_id", sessionResponse.data.pending_delivery_id);

        console.log(
          "✅ Session updated with backend response:",
          sessionResponse.data,
        );
      }

      // console.log("Full Session:", JSON.stringify(updatedSession, null, 2));

      // Send pre-approval
      const deliveryForPreApprove = updatedDeliveries.find(
        (d) => d.resident_token === currentResident.resident_token,
      );

      const preApprovePayload = {
        company_nm: session.company_nm || "",
        text_input_da_id: session.da_id,
        text_input_da_name: session.da_name || "",
        dp_created_at_date: session.created_at || "",
        text_input_file_ident_code: session.file_ident_code || "",
        complex_name: deliveryForPreApprove.complex_name || "",
        complex_location: deliveryForPreApprove.complex_location || "",
        resident_id: deliveryForPreApprove.resident_id?.toString() || "",
        resident_token: deliveryForPreApprove.resident_token || "",
        resident_phone_number:
          deliveryForPreApprove.resident_phone_number || "",
        text_input_resident_person_name:
          deliveryForPreApprove.resident_name || "",
        text_input_total_no_of_resident_parcel: persistedParcelCount.toString(),
        text_input_resident_req_otp:
          deliveryForPreApprove.resident_req_otp || "",
      };

      // Send pre-approval and check authentication
      const preApproveResponse =
        await sendPreApproveStatusAPI(preApprovePayload);

      // Check authentication from pre-approval response
      const isAuthenticated = preApproveResponse.data?.authentication === true;

      if (isAuthenticated) {
        // Authentication is true - set specific OTP fields from response
        console.log("✅ Authentication successful, setting OTP fields");

        // Extract OTP fields from the response if available
        const residentReqOtp =
          preApproveResponse.data?.resident_delivery?.resident_req_otp;
        const residentOtpValue =
          preApproveResponse.data?.resident_delivery?.resident_otp_value;

        // Update only the specific OTP fields in the delivery
        const updatedDeliveriesWithOTP = updatedDeliveries.map((delivery) =>
          delivery.resident_token === currentResident.resident_token
            ? {
                ...delivery,
                ...(residentReqOtp && { resident_req_otp: residentReqOtp }),
                ...(residentOtpValue && {
                  resident_otp_value: residentOtpValue,
                }),
                is_authenticated: "True",
              }
            : delivery,
        );

        setSession((prev) => ({
          ...prev,
          deliveries: updatedDeliveriesWithOTP,
        }));

        console.log("OTP fields set:", { residentReqOtp, residentOtpValue });
        toast.success(
          `Delivery approved! ${preApproveResponse.message || "Authentication successful"}`,
        );
      } else {
        // Send SMS
        try {
          if (deliveryForPreApprove.resident_phone_number) {
            await sendMessageAPI({
              phone_number: deliveryForPreApprove.resident_phone_number,
              message: `Hi ${deliveryForPreApprove.resident_name}, please approve your delivery for ${session.company_nm} using this link ${deliveryForPreApprove.resident_approval_link}.`,
            });
            toast.success("Approval link sent!");
          }
        } catch (err) {
          console.error("Failed to send approval message:", err);
          toast.error("Failed to send approval message.");
        }
      }

      toast.success(`Uploaded ${capturedCount} label(s)!`);

      setTimeout(() => {
        setIsLoading(false);
        setUploadInProgress(false);
        console.log(
          "Session updated on backend.",
          JSON.stringify(updatedSession, null, 2),
        );
        onNext();
      }, 1200);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(
        err.response?.data?.message || "Upload failed. Please try again.",
      );
      setIsLoading(false);
      setUploadInProgress(false);
    }
  };

  const handleRetake = () => {
    if (previews[currentIndex]) {
      URL.revokeObjectURL(previews[currentIndex]);
    }

    const newImages = [...images];
    newImages[currentIndex] = null;
    setImages(newImages);

    const newPreviews = [...previews];
    newPreviews[currentIndex] = null;
    setPreviews(newPreviews);

    const newImageSizes = [...imageSizes];
    newImageSizes[currentIndex] = null;
    setImageSizes(newImageSizes);
  };

  return (
    <div className="w-full min-h-screen px-4 sm:px-6 py-6 relative">
      {isLoading && <LoadingSpinner message={t("uploadingShippingLabels")} />}

      <CommonTitle showTitle={false} showBack={false} />

      <div className="flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-md p-6 w-full max-w-md space-y-4">
          <img
            src={previews[currentIndex] || "/placeholder-label.png"}
            alt={`Shipping Label ${currentIndex + 1}`}
            className="w-72 h-[380px] object-contain shadow-md border border-gray-300 rounded mb-4 mx-auto"
          />

          {imageSizes[currentIndex] && (
            <p className="text-sm text-gray-500 text-center">
              Size: {imageSizes[currentIndex]} MB
            </p>
          )}

          <p className="text-lg font-medium mb-2 text-center">
            {t("picture")} {currentIndex + 1} {t("of")} {persistedParcelCount}
          </p>

          <div className="flex gap-4 justify-center">
            {!images[currentIndex] ? (
              <button
                onClick={openCamera}
                className="bg-black text-white px-5 py-2 rounded-md font-semibold shadow"
                disabled={persistedParcelCount === 0}
              >
                {t("capture")}
              </button>
            ) : (
              <button
                onClick={handleRetake}
                className="bg-yellow-500 text-white px-5 py-2 rounded-md font-semibold shadow"
              >
                {t("retake")}
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={
                !images[currentIndex] || isLoading || persistedParcelCount === 0
              }
              className={`px-5 py-2 rounded-md font-semibold shadow ${
                images[currentIndex] && !isLoading && persistedParcelCount > 0
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {currentIndex < persistedParcelCount - 1
                ? t("next")
                : isLoading
                  ? t("uploading")
                  : t("done")}
            </button>
          </div>

          {persistedParcelCount === 0 && (
            <p className="text-red-500 text-center text-sm mt-2">
              No parcel count set. Please check resident data.
            </p>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCapture}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default ShippingLabelCaptureScreen;
